import type { NextPage, GetStaticProps } from 'next'
import { mediaClient } from 'src/apiMiddleware'
import { Params } from 'src/types'
import Container, { Props } from '@/containers/article/Details'
import Head from 'next/head'
import { ArticleDetailBase, WhitepaperDetailBase } from '@/route'
import { formatTime, replaceImg, readTime, tableOfContents, contentModify, wpContentModify } from '@/utils'
import { NewsArticle, WithContext } from 'schema-dts';
import PopupBanner from '@/components/PopupBanner'
import { useState } from 'react'

type StaticProps = Props & {
  structure: WithContext<NewsArticle>
}

export async function getStaticPaths() {
  const paths:any = [];
  let offset = 0
  do {
    const data = await mediaClient.articles.$get({ query: { limit: 100, offset } })
    data.contents.forEach((c) => paths.push({
      params: { id: c.id }
    }))
    offset += 100;
    if (offset >= data.totalCount) {
      break
    }
  } while (true) 

  return {
    paths,
    fallback: 'blocking',
  }
}
// おすすめをidで予め指定するか？
export const getStaticProps: GetStaticProps<StaticProps, Params> = async ({ params }) => {
  if (params?.id === undefined) {
    return {
      props: {},
      notFound: true,
    }
  }

  try {
    const detailPromise = mediaClient.articles._id(params.id).$get()
    const tagsPromise = mediaClient.tags.$get({ query: { filters: 'name[contains]おすすめ' } })
    const categoriesPromise = mediaClient.categories.$get({ query: { limit: 1000 } })

    const [data, tags, categories] = await Promise.all([detailPromise, tagsPromise, categoriesPromise])

    const recommendedPromise = mediaClient.articles.$get({
      query: { limit: 3, filters: `tags[contains]${tags.contents[0].id}` },
    })
    let relatedQueryString = ''
    data.tags.map((tag, idx) => {
      if (tag.name !== 'おすすめ') {
        relatedQueryString += `tags[contains]${tag.id}[or]`
      }
    })

    if (/.*\[or\]$/.test(relatedQueryString)) {
      relatedQueryString = relatedQueryString.slice(0, -"[or]".length)
    }

    const relatedArticlesPromise = mediaClient.articles.$get({ query: { limit: 3, filters: relatedQueryString } })

    const bannerPromise = mediaClient.banner.$get()

    const allTagsPromise = mediaClient.tags.$get({ query: { limit: 1000 } })

    const [recommendedArticles, relatedArticles, banner, allTags] = await Promise.all([recommendedPromise, relatedArticlesPromise, bannerPromise, allTagsPromise])

    let relatedWhitepapers = await mediaClient.whitepapers.$get({ query: { limit: 3, filters: relatedQueryString } })
    if (relatedWhitepapers.totalCount === 0) {
      relatedWhitepapers = await mediaClient.whitepapers.$get({ query: { limit: 3 } })
    }

    const breadcrumbs = [
      { title: 'Top', link: '/' },
      { title: '記事一覧', link: '/article/pages/1' },
      { title: data.title },
    ]

    const details = {
      ...data,
      tableOfContents: tableOfContents(data.body),
      body: replaceImg(data.body),
      revisedAt: formatTime(data.revisedAt),
      readTime: readTime(data.body),
    }

    const structure: WithContext<NewsArticle> = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "headline": details.title,
      "image": [
        `${details.eyeCatching.url}?width=${600}&height=${600}`,
        `${details.eyeCatching.url}?width=${800}&height=${600}`,
        `${details.eyeCatching.url}?width=${800}&height=${450}`,
      ],
      "datePublished": data.publishedAt,
      "dateModified": data.revisedAt,
      "author": [{
        "@type": "Organization",
        "name": "DXHUB inc.",
        "url": "https://dxhub.co.jp/"
      }]
    }

    return {
      props: {
        article: details,
        recommendedArticles: contentModify(recommendedArticles.contents, ArticleDetailBase),
        relatedArticles: contentModify(relatedArticles.contents, ArticleDetailBase),
        relatedWhitepapers: wpContentModify(relatedWhitepapers.contents, WhitepaperDetailBase),
        breadcrumbs,
        categories: categories.contents,
        banner,
        tags: allTags.contents,
        structure,
      },
    }
  } catch (e) {
    return {
      props: {},
      notFound: true,
    }
  }
}

const Pages: NextPage<StaticProps> = (props) => {
  const [showPopupBanner, setShowPopupBanner] = useState(true);

  const handleClosePopupBanner = () => {
    setShowPopupBanner(false);
  };  
  const { id, title, description, eyeCatching, tags } = props.article
  const keywords = tags.map((t) => t.name).join(',')
  const t = `${title}｜IoTBiz｜DXHUB株式会社`
  const metaImageUrl = eyeCatching.url + '?w=1200&h=630'
  return (
    <>
      <Head>
        <title key="title">{t}</title>
        <meta property="og:url" content={`https://iot.dxhub.co.jp/articles/${id}`} key="ou" />
        <meta property="og:title" content={t} key="ot" />
        <meta name="description" content={description} key="description" />
        <meta name="keywords" content={keywords} key="keywords" />
        <meta property="og:description" content={description} key="od" />
        <meta property="og:image" content={metaImageUrl} key="oi" />
        <meta name="twitter:image" content={metaImageUrl} key="ti" />
        <script
          key="ld-article"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.structure)
          }}
        >
        </script>
      </Head>
      <Container {...props} />
      

    </>
  )
}

export default Pages
